import type { PdiLevel } from '@felix/domain';
import {
  Alert,
  Button,
  Drawer,
  Group,
  Modal,
  NumberInput,
  Select,
  Switch,
  Textarea,
  TextInput,
} from '@mantine/core';
import {
  IconBooks,
  IconCheckupList,
  IconDownload,
  IconEdit,
  IconHierarchy2,
  IconPlus,
  IconSearch,
  IconStack2,
  IconUpload,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatTile } from '../../StatTile.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import lib from '../pms-library/PmsLibrary.module.css';
import { LoadError, TablePager, TableSkeleton } from '../purchasing/table.js';
import classes from './Drydock.module.css';
import { useDrydockErrorText } from './errors.js';
import type { DrydockLibrarySource } from './source.js';
import type { PdiLibrary, PdiNode, PdiNodeInput } from './types.js';
import {
  areaTree,
  filterCheckpoints,
  PAGE_SIZE,
  pageOf,
  pagesOf,
  type StatusFilter,
} from './view.js';

export interface DrydockLibraryScreenProps {
  source: DrydockLibrarySource;
}

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; library: PdiLibrary };

/** Düğüm çekmecesinin hedefi: yeni (düzey ve üstüyle) ya da var olan düğüm. */
type NodeTarget =
  | { mode: 'new'; level: PdiLevel; parentCode: string | null }
  | { mode: 'edit'; node: PdiNode };

/**
 * Maintenance (PMS) ▸ Dry Docking ▸ Library (ADR 0129 §3, §9): şirketin Pre-Docking Inspection
 * kütüphanesi. Solda alan ve sistem ağacı, sağda kontrol noktası tablosu, çekmecede kapsam,
 * yöntem, zamanlama ve kabul ölçütü. Ofiste düğüm yazılır ve sürüm yayımlanır; gemide salt okunur.
 */
export function DrydockLibraryScreen({ source }: DrydockLibraryScreenProps) {
  const { t } = useTranslation();
  const errorText = useDrydockErrorText();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<PdiNode | null>(null);
  const [target, setTarget] = useState<NodeTarget | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'green' | 'red'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', library: await source.load() });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);

  const office = source.scope === 'office';
  const library = state.kind === 'ready' ? state.library : null;
  const canCreate = office && (library?.can?.create ?? false) && Boolean(source.saveNode);
  const canEdit = office && (library?.can?.edit ?? false) && Boolean(source.saveNode);
  const canPublish = office && (library?.can?.publish ?? false) && Boolean(source.publish);

  async function loadDefaults() {
    if (!source.loadDefaults) return;
    setBusy(true);
    setNotice(null);
    try {
      const report = await source.loadDefaults();
      const added = report.areas + report.systems + report.checkpoints;
      const parts = [
        added > 0
          ? t('drydock.defaults.done', {
              version: report.version,
              areas: report.areas,
              systems: report.systems,
              checkpoints: report.checkpoints,
            })
          : t('drydock.defaults.none'),
        report.published !== null
          ? t('drydock.defaults.published', { version: report.published })
          : added > 0
            ? t('drydock.defaults.draft')
            : '',
      ];
      setNotice({ tone: 'green', text: parts.filter(Boolean).join(' ') });
      await load();
    } catch (error) {
      setNotice({ tone: 'red', text: errorText(error) });
    } finally {
      setBusy(false);
    }
  }

  const header = (
    <PageHeader
      title={t('drydock.library.title')}
      description={t(office ? 'drydock.library.description' : 'drydock.library.shipDescription')}
      actions={
        canCreate || canPublish ? (
          <Group gap="xs">
            {canCreate && source.loadDefaults ? (
              <Button
                variant="default"
                leftSection={<IconDownload size={16} aria-hidden />}
                loading={busy}
                onClick={() => void loadDefaults()}
              >
                {t('drydock.actions.loadDefaults')}
              </Button>
            ) : null}
            {canCreate ? (
              <Button
                variant="default"
                leftSection={<IconPlus size={16} aria-hidden />}
                onClick={() => setTarget({ mode: 'new', level: 'area', parentCode: null })}
              >
                {t('drydock.actions.newArea')}
              </Button>
            ) : null}
            {canPublish ? (
              <Button
                leftSection={<IconUpload size={16} aria-hidden />}
                onClick={() => setPublishing(true)}
              >
                {t('drydock.actions.publish')}
              </Button>
            ) : null}
          </Group>
        ) : undefined
      }
    />
  );

  if (state.kind === 'loading') {
    return (
      <div className={lib.screen}>
        {header}
        <TableSkeleton columns={5} label={t('drydock.library.title')} />
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className={lib.screen}>
        {header}
        <Panel>
          <LoadError
            code={state.code}
            title={t('drydock.library.error')}
            onRetry={() => {
              setState({ kind: 'loading' });
              void load();
            }}
          />
        </Panel>
      </div>
    );
  }

  const data = state.library;
  const live = data.nodes.filter((n) => n.active);
  const latest = data.versions[0] ?? null;
  const count = (level: PdiLevel) => live.filter((n) => n.level === level).length;

  return (
    <div className={lib.screen}>
      {header}
      <div className={lib.stats}>
        <StatTile
          icon={<IconStack2 size={20} />}
          label={t('drydock.stats.areas')}
          value={count('area')}
        />
        <StatTile
          icon={<IconHierarchy2 size={20} />}
          label={t('drydock.stats.systems')}
          value={count('system')}
        />
        <StatTile
          icon={<IconCheckupList size={20} />}
          label={t('drydock.stats.checkpoints')}
          value={count('checkpoint')}
          tone="success"
        />
        <StatTile
          icon={<IconBooks size={20} />}
          label={t('drydock.stats.version')}
          value={latest ? `v${latest.version}` : '—'}
        />
      </div>
      {/* Excel "Notes": taslak rehber, klas onaylı değil (ADR 0129 R18) */}
      <p className={classes.muted}>{t('drydock.library.disclaimer')}</p>
      {notice ? (
        <Alert color={notice.tone} withCloseButton onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      ) : null}
      {data.nodes.length > 0 && latest === null ? (
        // Sahip kararı 2026-10-26 (ADR 0129 Ek): varsayılan kütüphane taslak gelir; ilk sürümü
        // Superintendent gözden geçirip yayımlar, o zamana dek denetim açılamaz
        <Alert color="yellow" title={t('drydock.library.noVersion')}>
          <Group justify="space-between" gap="xs" wrap="wrap">
            <span>
              {t(canPublish ? 'drydock.library.draft' : 'drydock.library.draftNoPublish')}
            </span>
            {canPublish ? (
              <Button
                size="xs"
                leftSection={<IconUpload size={14} aria-hidden />}
                onClick={() => setPublishing(true)}
              >
                {t('drydock.actions.reviewPublish')}
              </Button>
            ) : null}
          </Group>
        </Alert>
      ) : data.unpublishedChanges ? (
        <Alert color="yellow">{t('drydock.library.unpublished')}</Alert>
      ) : null}
      <CheckpointsSplit
        library={data}
        selected={selected}
        onSelect={setSelected}
        canCreate={canCreate}
        canEdit={canEdit}
        onOpen={setDetail}
        onTarget={setTarget}
      />
      <VersionsPanel library={data} />
      <CheckpointDrawer
        node={detail}
        library={data}
        canEdit={canEdit}
        onClose={() => setDetail(null)}
        onEdit={(node) => {
          setDetail(null);
          setTarget({ mode: 'edit', node });
        }}
      />
      <NodeDrawer
        target={target}
        source={source}
        onClose={() => setTarget(null)}
        onSaved={async () => {
          setTarget(null);
          await load();
        }}
      />
      <PublishModal
        opened={publishing}
        library={data}
        source={source}
        onClose={() => setPublishing(false)}
        onDone={async (text) => {
          setPublishing(false);
          setNotice({ tone: 'green', text });
          await load();
        }}
      />
    </div>
  );
}

export function CheckpointsSplit({
  library,
  selected,
  onSelect,
  canCreate,
  canEdit,
  onOpen,
  onTarget,
}: {
  library: PdiLibrary;
  selected: string | null;
  onSelect: (code: string | null) => void;
  canCreate: boolean;
  canEdit: boolean;
  onOpen: (node: PdiNode) => void;
  onTarget: (target: NodeTarget) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);
  const tree = useMemo(() => areaTree(library.nodes), [library.nodes]);
  const names = useMemo(() => new Map(library.nodes.map((n) => [n.code, n.name])), [library.nodes]);
  const rows = useMemo(
    () => filterCheckpoints(library, { selected, query, status }),
    [library, selected, query, status],
  );
  const pages = pagesOf(rows.length);
  const current = Math.min(page, pages);
  const shown = pageOf(rows, current);
  const selectedNode = library.nodes.find((n) => n.code === selected) ?? null;
  const choose = (code: string | null) => {
    onSelect(code);
    setPage(1);
  };
  const total = library.nodes.filter((n) => n.level === 'checkpoint').length;
  const k = (key: string) => t(`drydock.columns.${key}`);

  return (
    <div className={lib.split}>
      <Panel title={t('drydock.library.tree')} flush>
        <nav className={lib.systemList} aria-label={t('drydock.library.tree')}>
          <button
            type="button"
            className={`${lib.systemButton} ${classes.treeButton}`}
            data-active={selected === null || undefined}
            aria-pressed={selected === null}
            onClick={() => choose(null)}
          >
            <span className={lib.systemCode}>—</span>
            <span className={lib.systemName}>{t('drydock.library.allAreas')}</span>
            <span className={lib.count}>{total}</span>
          </button>
          {tree.map(({ area, systems, count }) => (
            <div key={area.id}>
              <button
                type="button"
                className={`${lib.systemButton} ${classes.treeButton}`}
                data-active={selected === area.code || undefined}
                aria-pressed={selected === area.code}
                onClick={() => choose(area.code)}
                title={area.name}
              >
                <span className={`${lib.systemCode} ${classes.code}`}>{area.code.slice(4)}</span>
                <span className={`${lib.systemName} ${area.active ? '' : classes.inactiveName}`}>
                  {area.name}
                </span>
                <span className={lib.count}>{count}</span>
              </button>
              {systems.map(({ system, count: n }) => (
                <button
                  key={system.id}
                  type="button"
                  className={`${lib.systemButton} ${classes.treeButton} ${classes.subButton}`}
                  data-active={selected === system.code || undefined}
                  aria-pressed={selected === system.code}
                  onClick={() => choose(system.code)}
                  title={system.name}
                >
                  <span className={`${lib.systemCode} ${classes.code}`}>
                    {system.code.slice(4)}
                  </span>
                  <span
                    className={`${lib.systemName} ${system.active ? '' : classes.inactiveName}`}
                  >
                    {system.name}
                  </span>
                  <span className={lib.count}>{n}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>
      </Panel>
      <Panel title={t('drydock.library.checkpoints')} flush>
        <div className={lib.toolbar}>
          <TextInput
            className={lib.search}
            aria-label={t('drydock.search')}
            placeholder={t('drydock.search')}
            leftSection={<IconSearch size={16} aria-hidden />}
            value={query}
            onChange={(event) => {
              setQuery(event.currentTarget.value);
              setPage(1);
            }}
          />
          <Select
            className={lib.filter}
            aria-label={t('drydock.columns.status')}
            data={(['all', 'active', 'inactive'] as const).map((value) => ({
              value,
              label: t(`drydock.status.${value}`),
            }))}
            value={status}
            allowDeselect={false}
            onChange={(value) => {
              setStatus((value as StatusFilter | null) ?? 'all');
              setPage(1);
            }}
          />
          {selectedNode && canEdit ? (
            <Button
              variant="default"
              leftSection={<IconEdit size={16} aria-hidden />}
              onClick={() => onTarget({ mode: 'edit', node: selectedNode })}
            >
              {`${t('drydock.actions.edit')} ${selectedNode.code}`}
            </Button>
          ) : null}
          {selectedNode && canCreate && selectedNode.active ? (
            <Button
              leftSection={<IconPlus size={16} aria-hidden />}
              onClick={() =>
                onTarget({
                  mode: 'new',
                  level: selectedNode.level === 'area' ? 'system' : 'checkpoint',
                  parentCode: selectedNode.code,
                })
              }
            >
              {t(
                selectedNode.level === 'area'
                  ? 'drydock.actions.newSystem'
                  : 'drydock.actions.newCheckpoint',
              )}
            </Button>
          ) : null}
        </div>
        {shown.length === 0 ? (
          <EmptyState title={t(total === 0 ? 'drydock.empty.library' : 'drydock.empty.filtered')} />
        ) : (
          <section
            className={table.tableWrap}
            aria-label={t('drydock.library.checkpointList')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kayan tablo klavyeyle kaydırılır
            tabIndex={0}
          >
            <table className={`${table.table} ${classes.checkpointTable}`}>
              <thead>
                <tr>
                  <th scope="col">{k('code')}</th>
                  <th scope="col">{k('checkpoint')}</th>
                  <th scope="col">{k('system')}</th>
                  <th scope="col">{k('method')}</th>
                  <th scope="col">{k('status')}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((node) => (
                  <tr key={node.id}>
                    <td className={classes.mono}>{node.code}</td>
                    <td>
                      <button
                        type="button"
                        className={classes.linkButton}
                        onClick={() => onOpen(node)}
                      >
                        {node.name}
                      </button>
                    </td>
                    <td>{names.get(node.parentCode ?? '') ?? '—'}</td>
                    <td>{node.content?.method ?? '—'}</td>
                    <td>
                      <StatusBadge tone={node.active ? 'success' : 'neutral'}>
                        {t(`drydock.status.${node.active ? 'active' : 'inactive'}`)}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
        {rows.length > PAGE_SIZE ? (
          <TablePager
            page={current}
            pages={pages}
            label={t('drydock.library.checkpointList')}
            summary={t('drydock.showing', {
              from: (current - 1) * PAGE_SIZE + 1,
              to: Math.min(rows.length, current * PAGE_SIZE),
              total: rows.length,
            })}
            onPage={setPage}
          />
        ) : null}
      </Panel>
    </div>
  );
}

export function VersionsPanel({ library }: { library: PdiLibrary }) {
  const { t } = useTranslation();
  const k = (key: string) => t(`drydock.columns.${key}`);
  return (
    <Panel title={t('drydock.library.versions')} flush>
      {library.versions.length === 0 ? (
        <EmptyState title={t('drydock.empty.versions')} />
      ) : (
        <section
          className={table.tableWrap}
          aria-label={t('drydock.library.versions')}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kayan tablo klavyeyle kaydırılır
          tabIndex={0}
        >
          <table className={table.table}>
            <thead>
              <tr>
                <th scope="col">{k('version')}</th>
                <th scope="col">{k('label')}</th>
                <th scope="col">{t('drydock.stats.checkpoints')}</th>
                <th scope="col">{k('note')}</th>
                <th scope="col">{k('publishedAt')}</th>
                <th scope="col">{k('publishedBy')}</th>
              </tr>
            </thead>
            <tbody>
              {library.versions.map((v) => (
                <tr key={v.id}>
                  <td className={classes.mono}>v{v.version}</td>
                  <td>{v.label}</td>
                  <td>{v.checkpoints}</td>
                  <td>{v.note ?? '—'}</td>
                  <td>{formatDateTime(v.createdAt)}</td>
                  <td>{v.createdByName ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </Panel>
  );
}

function CheckpointDrawer({
  node,
  library,
  canEdit,
  onClose,
  onEdit,
}: {
  node: PdiNode | null;
  library: PdiLibrary;
  canEdit: boolean;
  onClose: () => void;
  onEdit: (node: PdiNode) => void;
}) {
  const { t } = useTranslation();
  const system = library.nodes.find((n) => n.code === node?.parentCode);
  const area = library.nodes.find((n) => n.code === system?.parentCode);
  const fields = ['scope', 'method', 'timing', 'acceptance'] as const;
  return (
    <Drawer
      opened={node !== null}
      onClose={onClose}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
      position="right"
      size="lg"
      title={node ? `${node.code} · ${node.name}` : t('drydock.detail.title')}
    >
      {node ? (
        <div className={classes.detail}>
          <p className={classes.path}>
            {t('drydock.detail.path', { area: area?.name ?? '—', system: system?.name ?? '—' })}
          </p>
          <StatusBadge tone={node.active ? 'success' : 'neutral'}>
            {t(`drydock.status.${node.active ? 'active' : 'inactive'}`)}
          </StatusBadge>
          <dl className={classes.fields}>
            {fields.map((field) => (
              <div key={field}>
                <dt>{t(`drydock.fields.${field}`)}</dt>
                <dd>{node.content?.[field] ?? '—'}</dd>
              </div>
            ))}
          </dl>
          {canEdit ? (
            <Group justify="flex-end">
              <Button
                variant="default"
                leftSection={<IconEdit size={16} aria-hidden />}
                onClick={() => onEdit(node)}
              >
                {t('drydock.actions.edit')}
              </Button>
            </Group>
          ) : null}
        </div>
      ) : null}
    </Drawer>
  );
}

interface NodeForm {
  name: string;
  sortOrder: number;
  active: boolean;
  scope: string;
  method: string;
  timing: string;
  acceptance: string;
}

function formOf(target: NodeTarget | null): NodeForm {
  const node = target?.mode === 'edit' ? target.node : null;
  return {
    name: node?.name ?? '',
    sortOrder: node?.sortOrder ?? 0,
    active: node?.active ?? true,
    scope: node?.content?.scope ?? '',
    method: node?.content?.method ?? '',
    timing: node?.content?.timing ?? '',
    acceptance: node?.content?.acceptance ?? '',
  };
}

function NodeDrawer({
  target,
  source,
  onClose,
  onSaved,
}: {
  target: NodeTarget | null;
  source: DrydockLibrarySource;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = useDrydockErrorText();
  const [form, setForm] = useState<NodeForm>(() => formOf(target));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm(formOf(target));
    setError(null);
  }, [target]);

  const level: PdiLevel | null =
    target === null ? null : target.mode === 'new' ? target.level : target.node.level;
  const parentCode =
    target === null ? null : target.mode === 'new' ? target.parentCode : target.node.parentCode;
  const set = <K extends keyof NodeForm>(key: K, value: NodeForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  async function save() {
    if (!target || !level || !source.saveNode) return;
    setSaving(true);
    setError(null);
    const node: PdiNodeInput = {
      ...(target.mode === 'edit' ? { code: target.node.code } : {}),
      level,
      parentCode,
      name: form.name,
      sortOrder: form.sortOrder,
      active: form.active,
      content:
        level === 'checkpoint'
          ? {
              scope: form.scope,
              method: form.method,
              timing: form.timing,
              acceptance: form.acceptance,
            }
          : null,
    };
    try {
      await source.saveNode(
        target.mode === 'edit'
          ? { id: target.node.id, rowVersion: target.node.rowVersion, node }
          : { node },
      );
      await onSaved();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  }

  const levelLabel = level ? t(`drydock.levels.${level}`) : '';
  return (
    <Drawer
      opened={target !== null}
      onClose={onClose}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
      position="right"
      size="lg"
      title={
        target?.mode === 'edit'
          ? t('drydock.node.titleEdit', { code: target.node.code })
          : t('drydock.node.titleNew', { level: levelLabel })
      }
    >
      <div className={classes.form}>
        <Alert color="gray">
          {target?.mode === 'edit'
            ? t('drydock.node.fixedCode')
            : t('drydock.node.autoCode', { parent: parentCode ?? 'PDI' })}
        </Alert>
        {error ? <Alert color="red">{error}</Alert> : null}
        <TextInput
          label={t('drydock.fields.name')}
          required
          value={form.name}
          onChange={(e) => set('name', e.currentTarget.value)}
        />
        <NumberInput
          label={t('drydock.fields.sortOrder')}
          min={0}
          value={form.sortOrder}
          onChange={(value) => set('sortOrder', typeof value === 'number' ? value : 0)}
        />
        {level === 'checkpoint' ? (
          <>
            <Textarea
              label={t('drydock.fields.scope')}
              required
              autosize
              minRows={2}
              value={form.scope}
              onChange={(e) => set('scope', e.currentTarget.value)}
            />
            <TextInput
              label={t('drydock.fields.method')}
              required
              value={form.method}
              onChange={(e) => set('method', e.currentTarget.value)}
            />
            <TextInput
              label={t('drydock.fields.timing')}
              required
              value={form.timing}
              onChange={(e) => set('timing', e.currentTarget.value)}
            />
            <Textarea
              label={t('drydock.fields.acceptance')}
              required
              autosize
              minRows={2}
              value={form.acceptance}
              onChange={(e) => set('acceptance', e.currentTarget.value)}
            />
          </>
        ) : null}
        {target?.mode === 'edit' ? (
          <Switch
            label={t('drydock.fields.active')}
            checked={form.active}
            onChange={(e) => set('active', e.currentTarget.checked)}
          />
        ) : null}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('drydock.cancel')}
          </Button>
          <Button loading={saving} onClick={() => void save()}>
            {t('drydock.save')}
          </Button>
        </Group>
      </div>
    </Drawer>
  );
}

function PublishModal({
  opened,
  library,
  source,
  onClose,
  onDone,
}: {
  opened: boolean;
  library: PdiLibrary;
  source: DrydockLibrarySource;
  onClose: () => void;
  onDone: (text: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = useDrydockErrorText();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (opened) {
      setNote('');
      setError(null);
    }
  }, [opened]);
  const next = (library.versions[0]?.version ?? 0) + 1;

  async function publish() {
    if (!source.publish) return;
    setSaving(true);
    setError(null);
    try {
      const result = await source.publish(note.trim() || null);
      await onDone(
        result.changed
          ? t('drydock.publish.done', { version: result.version.version })
          : t('drydock.publish.unchanged', { version: result.version.version }),
      );
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('drydock.publish.title')}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
    >
      <div className={classes.form}>
        <p>{t('drydock.publish.body', { version: next })}</p>
        {error ? <Alert color="red">{error}</Alert> : null}
        <Textarea
          label={t('drydock.fields.note')}
          description={t('drydock.publish.noteHint')}
          required={next > 1}
          autosize
          minRows={2}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('drydock.cancel')}
          </Button>
          <Button loading={saving} onClick={() => void publish()}>
            {t('drydock.actions.publish')}
          </Button>
        </Group>
      </div>
    </Modal>
  );
}
